import { useNavigate, useSearch } from "@tanstack/react-router";
import { ViewToggle } from "@/components/ui/PageHeader/ViewToggle";
import type { ViewOption } from "@/components/ui/PageHeader/types";
import type { AuditDetailTab } from "@/packages/types";
import { useTranslation } from "@/packages/utils";

export const AUDIT_DETAIL_TABS = [
  "overview",
  "soa",
  "checklist",
  "documents",
  "clauses",
  "findings",
  "activity",
] as const;

interface TabNavigationOptions {
  /** When true, the SoA tab is shown. Defaults to false. */
  showSoaTab?: boolean;
}

export function useTabNavigation(options?: TabNavigationOptions) {
  const { t } = useTranslation(["audits", "soa"]);
  const { tab } = useSearch({ from: "/$workspaceSlug/audits/$auditId/" });
  const navigate = useNavigate({ from: "/$workspaceSlug/audits/$auditId/" });
  const showSoa = options?.showSoaTab ?? false;

  const visibleTabs = showSoa
    ? AUDIT_DETAIL_TABS
    : AUDIT_DETAIL_TABS.filter((id) => id !== "soa");

  const viewOptions: ViewOption[] = visibleTabs.map((id) => ({
    id,
    label: id === "soa" ? t("soa:title") : t(`audits:detail.tabs.${id}`),
    active: id === (tab ?? "overview"),
  }));

  const handleViewChange = (id: string) => {
    navigate({ search: { tab: id as AuditDetailTab }, replace: true });
  };

  return { viewOptions, handleViewChange };
}

export function AuditDetailTabs() {
  const { viewOptions, handleViewChange } = useTabNavigation();
  return <ViewToggle options={viewOptions} onViewChange={handleViewChange} />;
}
